import { getApperClient } from '@/services/apperClient';

/**
 * Service for managing Facebook access tokens in the database
 * Uses access_token_c table with fields: token_c, expires_at_c, user_identifier_c, Name
 */
class AccessTokenService {
  constructor() {
    this.tableName = 'access_token_c';
  }

  /**
   * Save or update access token for a user
   * @param {string} userIdentifier - Facebook user ID
   * @param {string} token - Facebook access token
   * @param {number} expiresAt - Expiration timestamp (milliseconds)
   * @returns {Promise<Object>} Created or updated token record
   */
  async saveAccessToken(userIdentifier, token, expiresAt) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error('ApperClient not available');
      }

      // Check if token already exists for this user
      const existingToken = await this.getAccessToken(userIdentifier);

      if (existingToken) {
        // Update existing token
        const params = {
          records: [{
            Id: existingToken.Id,
            token_c: token,
            expires_at_c: new Date(expiresAt).toISOString(),
            user_identifier_c: userIdentifier,
            Name: `Facebook Token - ${userIdentifier}`
          }]
        };

        const response = await apperClient.updateRecord(this.tableName, params);

        if (!response.success) {
          console.error('ACCESS_TOKEN_ERROR: Failed to update access token:', response);
          return { success: false, error: response.message || 'Failed to update access token' };
        }

        if (response.results && response.results.length > 0) {
          const successful = response.results.filter(r => r.success);
          const failed = response.results.filter(r => !r.success);
          
          if (failed.length > 0) {
            console.error(`ACCESS_TOKEN_ERROR: Failed to update ${failed.length} records:`, failed);
            failed.forEach(record => {
              if (record.message) {
                console.error('ACCESS_TOKEN_ERROR: Record update failed:', record.message);
              }
            });
          }
          
          if (successful.length > 0) {
            return { success: true, data: successful[0].data };
          }
        }

        return { success: false, error: 'No successful token update' };
      } else {
        // Create new token
        const params = {
          records: [{
            token_c: token,
            expires_at_c: new Date(expiresAt).toISOString(),
            user_identifier_c: userIdentifier,
            Name: `Facebook Token - ${userIdentifier}`
          }]
        };

        const response = await apperClient.createRecord(this.tableName, params);

        if (!response.success) {
          console.error('ACCESS_TOKEN_ERROR: Failed to create access token:', response);
          return { success: false, error: response.message || 'Failed to create access token' };
        }

        if (response.results && response.results.length > 0) {
          const successful = response.results.filter(r => r.success);
          const failed = response.results.filter(r => !r.success);
          
          if (failed.length > 0) {
            console.error(`ACCESS_TOKEN_ERROR: Failed to create ${failed.length} records:`, failed);
            failed.forEach(record => {
              if (record.message) {
                console.error('ACCESS_TOKEN_ERROR: Record creation failed:', record.message);
              }
            });
          }
          
          if (successful.length > 0) {
            return { success: true, data: successful[0].data };
          }
        }

        return { success: false, error: 'No successful token creation' };
      }
    } catch (error) {
      console.error('ACCESS_TOKEN_ERROR: Exception in saveAccessToken:', error);
      return { success: false, error: error.message || 'Database operation failed' };
    }
  }

  /**
   * Retrieve access token for a user
   * @param {string} userIdentifier - Facebook user ID
   * @returns {Promise<Object|null>} Token record or null if not found/expired
   */
  async getAccessToken(userIdentifier) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error('ApperClient not available');
      }

      const params = {
        fields: [
          {"field": {"Name": "Id"}},
          {"field": {"Name": "token_c"}},
          {"field": {"Name": "expires_at_c"}},
          {"field": {"Name": "user_identifier_c"}},
          {"field": {"Name": "Name"}}
        ],
        where: [{
          "FieldName": "user_identifier_c",
          "Operator": "EqualTo",
          "Values": [userIdentifier],
          "Include": true
        }],
        orderBy: [{
          "fieldName": "CreatedOn",
          "sorttype": "DESC"
        }],
        pagingInfo: {
          "limit": 1,
          "offset": 0
        }
      };

      const response = await apperClient.fetchRecords(this.tableName, params);

      if (!response.success) {
        console.error('Failed to fetch access token:', response);
        return null;
      }

      if (!response.data || response.data.length === 0) {
        return null;
      }

      const tokenRecord = response.data[0];

      // Check if token is expired
      if (tokenRecord.expires_at_c) {
        const expirationDate = new Date(tokenRecord.expires_at_c);
        const now = new Date();
        
        if (now >= expirationDate) {
          console.log('Token expired, removing from database');
          await this.deleteAccessToken(userIdentifier);
          return null;
        }
      }

      return {
        Id: tokenRecord.Id,
        token: tokenRecord.token_c,
        expiresAt: tokenRecord.expires_at_c ? new Date(tokenRecord.expires_at_c).getTime() : null,
        userIdentifier: tokenRecord.user_identifier_c
      };
    } catch (error) {
      console.error('Error fetching access token:', error);
      return null;
    }
  }

  /**
   * Delete access token for a user
   * @param {string} userIdentifier - Facebook user ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteAccessToken(userIdentifier) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error('ApperClient not available');
      }

      // First find the token record
      const tokenRecord = await this.getAccessToken(userIdentifier);
      if (!tokenRecord) {
        return true; // Already deleted or doesn't exist
      }

      const params = {
        RecordIds: [tokenRecord.Id]
      };

      const response = await apperClient.deleteRecord(this.tableName, params);

      if (!response.success) {
        console.error('Failed to delete access token:', response);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting access token:', error);
      return false;
    }
  }

  /**
   * Delete all expired tokens (cleanup utility)
   * @returns {Promise<number>} Number of tokens deleted
   */
  async deleteExpiredTokens() {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error('ApperClient not available');
      }

      const now = new Date().toISOString();
      
      const params = {
        fields: [
          {"field": {"Name": "Id"}},
          {"field": {"Name": "expires_at_c"}}
        ],
        where: [{
          "FieldName": "expires_at_c",
          "Operator": "LessThan",
          "Values": [now],
          "Include": true
        }]
      };

      const response = await apperClient.fetchRecords(this.tableName, params);

      if (!response.success || !response.data || response.data.length === 0) {
        return 0;
      }

      const expiredIds = response.data.map(record => record.Id);
      
      const deleteParams = {
        RecordIds: expiredIds
      };

      const deleteResponse = await apperClient.deleteRecord(this.tableName, deleteParams);

      if (!deleteResponse.success) {
        console.error('Failed to delete expired tokens:', deleteResponse);
        return 0;
      }

      return expiredIds.length;
    } catch (error) {
      console.error('Error deleting expired tokens:', error);
      return 0;
    }
  }
}

export const accessTokenService = new AccessTokenService();
export default accessTokenService;